import { useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';

export interface Tag {
  id: string;
  name: string;
  slug: string;
  created_at: string;
}

const generateSlug = (name: string): string => {
  return name
    .toLowerCase()
    .trim()
    .replace(/[^\w\s-]/g, '')
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-');
};

export const useTags = () => {
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);

  const fetchAllTags = async (): Promise<Tag[]> => {
    try {
      const { data, error } = await supabase
        .from('tags')
        .select('*')
        .order('name', { ascending: true });

      if (error) throw error;
      return data || [];
    } catch (error) {
      console.error('Error fetching tags:', error);
      toast({
        title: "Error fetching tags",
        description: "Could not load tags. Please try again.",
        variant: "destructive",
      });
      return [];
    }
  };

  const searchTags = async (query: string): Promise<Tag[]> => {
    if (!query.trim()) return [];
    
    try {
      const { data, error } = await supabase
        .from('tags')
        .select('*')
        .ilike('name', `%${query}%`)
        .order('name', { ascending: true })
        .limit(10);

      if (error) throw error;
      return data || [];
    } catch (error) {
      console.error('Error searching tags:', error);
      return [];
    }
  };

  const createTag = async (name: string): Promise<Tag | null> => {
    const trimmedName = name.trim();
    
    if (trimmedName.length < 2) {
      toast({
        title: "Tag name too short",
        description: "Tag name must be at least 2 characters.",
        variant: "destructive",
      });
      return null;
    }

    if (trimmedName.length > 50) {
      toast({
        title: "Tag name too long",
        description: "Tag name must be less than 50 characters.",
        variant: "destructive",
      });
      return null;
    }

    setLoading(true);
    try {
      const slug = generateSlug(trimmedName);
      
      const { data, error } = await supabase
        .from('tags')
        .insert({ name: trimmedName, slug })
        .select()
        .single();

      if (error) {
        if (error.code === '23505') {
          toast({
            title: "Tag already exists",
            description: "A tag with this name already exists.",
            variant: "destructive",
          });
        } else {
          throw error;
        }
        return null;
      }

      toast({
        title: "Tag created",
        description: `Created tag "${trimmedName}"`,
      });
      return data;
    } catch (error) {
      console.error('Error creating tag:', error);
      toast({
        title: "Error creating tag",
        description: "Could not create tag. Please try again.",
        variant: "destructive",
      });
      return null;
    } finally {
      setLoading(false);
    }
  };

  const fetchPostTags = async (postId: string): Promise<Tag[]> => {
    try {
      const { data, error } = await supabase
        .from('post_tags')
        .select('tag_id, tags(*)')
        .eq('blog_post_id', postId);

      if (error) throw error;
      return data?.map(pt => pt.tags as unknown as Tag) || [];
    } catch (error) {
      console.error('Error fetching post tags:', error);
      return [];
    }
  };

  const updatePostTags = async (postId: string, tagIds: string[]): Promise<boolean> => {
    setLoading(true);
    try {
      // Delete existing post-tag associations
      const { error: deleteError } = await supabase
        .from('post_tags')
        .delete()
        .eq('blog_post_id', postId);

      if (deleteError) throw deleteError;

      // Insert new associations
      if (tagIds.length > 0) {
        const postTags = tagIds.map(tagId => ({
          blog_post_id: postId,
          tag_id: tagId,
        }));

        const { error: insertError } = await supabase
          .from('post_tags')
          .insert(postTags);

        if (insertError) throw insertError;
      }

      return true;
    } catch (error) {
      console.error('Error updating post tags:', error);
      toast({
        title: "Error updating tags",
        description: "Could not update post tags. Please try again.",
        variant: "destructive",
      });
      return false;
    } finally {
      setLoading(false);
    }
  };

  const deleteTag = async (tagId: string): Promise<boolean> => {
    setLoading(true);
    try {
      const { error } = await supabase
        .from('tags')
        .delete()
        .eq('id', tagId);

      if (error) throw error;

      toast({
        title: "Tag deleted",
        description: "Tag has been deleted successfully.",
      });
      return true;
    } catch (error) {
      console.error('Error deleting tag:', error);
      toast({
        title: "Error deleting tag",
        description: "Could not delete tag. Please try again.",
        variant: "destructive",
      });
      return false;
    } finally {
      setLoading(false);
    }
  };

  return {
    loading,
    fetchAllTags,
    searchTags,
    createTag,
    fetchPostTags,
    updatePostTags,
    deleteTag,
  };
};
